// Pure helpers for the AHA-6681 tonnage feature: the want-card SYSTEM SIZE
// tile row (contiguous-span two-tap interaction), equipment/calc derivation,
// the most-recent-explicit-wins precedence, the L&S conflict predicate and
// its dialog resolution, and the Choose Systems filter seed. SQL/UI-free so
// every rule is unit-testable; the state machinery stays in
// recommendations-strip.tsx and the tonnage hooks.

import {
  parseTonnageRecord,
  TONNAGE_TILE_TONS,
  type SizingPath,
  type TonnageRecord,
  type TonnageSource
} from '@alpinehomeair/common/build/helpers/tonnage-record';

export {
  SIZING_PATHS,
  TONNAGE_SOURCES,
  TONNAGE_TILE_TONS
} from '@alpinehomeair/common/build/helpers/tonnage-record';
export type {
  SizingPath,
  TonnageRecord,
  TonnageSource
} from '@alpinehomeair/common/build/helpers/tonnage-record';

// The resolved display/selection state: what the tile row lights up and what
// the CS filter seeds from, regardless of whether it came from a record or a
// live derivation.
export type EffectiveTonnage = {
  tons: number[];
  source: TonnageSource;
};

// Equipment-derived tonnage (raw kept for the honest hint — all observed
// values sit on the scale, but an off-scale one maps to the nearest tile
// with the raw value stated).
export type EquipmentTonnage = {
  tile: number;
  raw: number;
  unitLabel: string;
  modelNumber: string | null;
};

// L&S calc tonnage: totalBTUs.cooling ÷ 12,000 → nearest tile, with the BTU
// figure kept for the visible mapping hint, and `tons` the contiguous span
// of nominal sizes the load actually takes (see calcTonnageSpan) — what the
// tile row lights and the Choose Systems chip filters to when the calc is
// the size story.
export type CalcTonnage = {
  tile: number;
  tons: number[];
  btu: number;
};

type TonnageFlagCarry = {
  resolvedCalcTons?: number | null;
  sizingPath?: SizingPath | null;
  filterDismissed?: boolean;
};

// Sticky fields that survive a size rewrite. Omitted keys stay omitted so a
// re-persist never fabricates undefined.
export function carryTonnageFlags(
  record: TonnageRecord | null | undefined
): TonnageFlagCarry {
  return {
    ...(record?.resolvedCalcTons !== undefined
      ? { resolvedCalcTons: record.resolvedCalcTons }
      : {}),
    ...(record?.sizingPath !== undefined
      ? { sizingPath: record.sizingPath }
      : {}),
    ...(record?.filterDismissed !== undefined
      ? { filterDismissed: record.filterDismissed }
      : {})
  };
}

// 'use-equipment' is only truthful while the tons ARE the equipment size.
// Any write that moves the tons off that size (tile tap, adopting the calc)
// degrades the path to 'use-tonnage' — the record just stops claiming
// "same as old equipment" (the Choose Systems filter reads that path,
// resolveFilterEffectiveTonnage). Keep-current on the conflict dialog does
// not call this: it keeps the equipment size, so it keeps the path. New
// size-mutation paths should spread this instead of re-deriving the
// rewrite.
export function withSizingPathAfterManualSize(
  record: TonnageRecord | null | undefined
): TonnageFlagCarry {
  const flags = carryTonnageFlags(record);
  if (record?.sizingPath !== 'use-equipment') return flags;
  return { ...flags, sizingPath: 'use-tonnage' };
}

export function isUnsureTonnage(
  record: TonnageRecord | null | undefined
): boolean {
  return record?.sizingPath === 'block-load';
}

export function visibleTonnageSelection(
  effective: EffectiveTonnage | null,
  record: TonnageRecord | null | undefined
): number[] {
  if (isUnsureTonnage(record)) return [];
  return effective?.tons ?? [];
}

export function sizingPathAfterSizeTile(
  tons: number[],
  record: TonnageRecord | null | undefined,
  pathChoicesEnabled: boolean
): TonnageFlagCarry {
  const flags = withSizingPathAfterManualSize(record);
  if (!pathChoicesEnabled) return flags;
  return { ...flags, sizingPath: tons.length > 0 ? 'use-tonnage' : null };
}

export function toggleUnsureTonnageRecord(
  record: TonnageRecord | null | undefined
): TonnageRecord {
  if (isUnsureTonnage(record)) {
    return {
      tons: [],
      source: 'manual',
      ...carryTonnageFlags(record),
      sizingPath: null
    };
  }
  return {
    tons: [],
    source: 'manual',
    ...carryTonnageFlags(record),
    sizingPath: 'block-load'
  };
}

const sortedUnique = (values: number[]): number[] =>
  [...new Set(values)].sort((a, b) => a - b);

// Every scale tile within [lo, hi] inclusive — the only way selections are
// ever built, which is what makes non-adjacent multi-select impossible.
export function tonnageSpan(a: number, b: number): number[] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return TONNAGE_TILE_TONS.filter((value) => value >= lo && value <= hi);
}

// Contiguous-span two-tap interaction (§A, mock S1–S3):
// - empty + tap → single value;
// - tap outside the span → the whole span between lights up;
// - tap a selected endpoint → it drops off (span shrinks inward);
// - tap an interior tile of a range → collapse to that single tile;
// - tap the only selected tile → clear.
// A legacy non-contiguous record is treated as its [min, max] span.
export function toggleTonnageTile(current: number[], value: number): number[] {
  const selected = sortedUnique(current);
  if (selected.length === 0) return [value];
  const min = selected[0];
  const max = selected[selected.length - 1];
  const span = tonnageSpan(min, max);
  if (value >= min && value <= max) {
    if (span.length === 1) return [];
    if (value === min || value === max) {
      return span.filter((tile) => tile !== value);
    }
    return [value];
  }
  return tonnageSpan(Math.min(min, value), Math.max(max, value));
}

// Live readout label ("3 Ton", "2.5 – 3.5 Ton"); a degenerate span renders
// as the single value — never "3 – 3".
export function tonnageRangeLabel(tons: number[]): string {
  const selected = sortedUnique(tons);
  if (selected.length === 0) return '';
  const min = selected[0];
  const max = selected[selected.length - 1];
  return min === max ? `${min} Ton` : `${min} – ${max} Ton`;
}

// Nearest tile on the scale; an exact midpoint sizes UP (undersizing is the
// costlier miss for cooling equipment).
export function nearestTonnageTile(rawTons: number): number {
  let nearest = TONNAGE_TILE_TONS[0];
  for (const tile of TONNAGE_TILE_TONS) {
    if (Math.abs(tile - rawTons) <= Math.abs(nearest - rawTons)) {
      nearest = tile;
    }
  }
  return nearest;
}

const BTU_PER_TON = 12000;

// The sizes Manual S can select for a cooling load, as percent of the load:
// air conditioners 95–115%, heat pumps 100–125%, widened five points each
// way because a nominal size's rated capacity lands a few percent off its
// tile (a "3-ton" rates 34,000–37,000). The server enforces the exact bands
// against rated capacities; this envelope only has to make sure the size
// chip never excludes a size those bands could admit.
const CALC_SIZE_ENVELOPE = { lowerPercent: 90, upperPercent: 130 };

// The nominal sizes inside the load's Manual S envelope — the sizes it
// takes on the tile scale — falling back to the nearest tile when the
// envelope straddles a gap between sizes or runs off the scale.
export function calcTonnageSpan(coolingBtu: number): number[] {
  const lowerTons =
    (coolingBtu * CALC_SIZE_ENVELOPE.lowerPercent) / 100 / BTU_PER_TON;
  const upperTons =
    (coolingBtu * CALC_SIZE_ENVELOPE.upperPercent) / 100 / BTU_PER_TON;
  const inside = TONNAGE_TILE_TONS.filter(
    (tile) => tile >= lowerTons && tile <= upperTons
  );
  return inside.length > 0
    ? inside
    : [nearestTonnageTile(coolingBtu / BTU_PER_TON)];
}

// L&S calc mapping: totalBTUs.cooling (block-load cooling + latent, the
// same composition the CCB band uses) ÷ 12,000 → nearest tile, plus the
// span of sizes the load takes. Zero / missing / non-finite BTU means "no
// calc yet" — the watcher publishes 0 while the structure isn't sizable.
export function calcTonnageFromBtu(
  coolingBtu: number | null | undefined
): CalcTonnage | null {
  if (coolingBtu == null || !Number.isFinite(coolingBtu) || coolingBtu <= 0) {
    return null;
  }
  return {
    tile: nearestTonnageTile(coolingBtu / BTU_PER_TON),
    tons: calcTonnageSpan(coolingBtu),
    btu: coolingBtu
  };
}

function readPositiveSpec(
  component: { [key: string]: unknown },
  key: 'tonnage' | 'coolingCapacity'
): number | null {
  const raw = component[key];
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function readComponentTonnage(component: {
  [key: string]: unknown;
}): number | null {
  const tonnage = readPositiveSpec(component, 'tonnage');
  if (tonnage != null) return tonnage;
  const coolingCapacity = readPositiveSpec(component, 'coolingCapacity');
  return coolingCapacity == null
    ? null
    : Math.round((coolingCapacity / BTU_PER_TON) * 2) / 2;
}

const OUTDOOR_UNIT_LABELS: Record<string, string> = {
  'Split System Condenser': 'Air Conditioner',
  'Split System Heat Pump': 'Heat Pump',
  'Next-Gen Condenser': 'Air Conditioner',
  'Next-Gen Heat Pump': 'Heat Pump'
};

const PACKAGED_UNIT_LABELS: Record<string, string> = {
  'Self Contained Electric AC': 'Packaged AC',
  'Self Contained Electric Heat Pump': 'Packaged Heat Pump',
  'Self Contained Gas Pack With AC': 'Gas Pack',
  'Self Contained Gas Pack With Heat Pump': 'Gas Pack'
};

type TonnageSourceComponent = {
  type?: string;
  replacing?: boolean;
  modelNumber?: string;
  [key: string]: unknown;
};

// Equipment derivation (§B rows 1–2): the outdoor unit's size pre-selects
// (tonnage, or coolingCapacity BTU mapped onto the half-ton scale — label
// photos often carry BTU and the analyzer is forbidden from inventing
// tons), replacing-over-kept preference (mirroring refrigerant derivation),
// two units in the preferred bucket disagreeing → honest none. Packaged
// unit size is the fallback ONLY when no split outdoor exists. Coils
// deliberately contribute nothing (they accommodate, they don't size),
// and virtual-tile haves have no components at all — never a source.
export function deriveEquipmentTonnage(
  components: TonnageSourceComponent[] | null | undefined
): EquipmentTonnage | null {
  const all = components ?? [];
  const pickFrom = (
    pool: TonnageSourceComponent[],
    labels: Record<string, string>
  ): EquipmentTonnage | null => {
    const withTonnage = pool.filter(
      (component) => readComponentTonnage(component) != null
    );
    const replacing = withTonnage.filter((component) => component.replacing);
    const bucket = replacing.length > 0 ? replacing : withTonnage;
    const values = sortedUnique(
      bucket.map((component) => readComponentTonnage(component) as number)
    );
    if (values.length !== 1) return null;
    const component = bucket[0];
    const raw = values[0];
    return {
      tile: nearestTonnageTile(raw),
      raw,
      unitLabel: labels[component.type ?? ''] ?? 'outdoor unit',
      modelNumber: component.modelNumber || null
    };
  };
  const outdoor = all.filter(
    (component) => OUTDOOR_UNIT_LABELS[component.type ?? ''] !== undefined
  );
  if (outdoor.length > 0) {
    return pickFrom(outdoor, OUTDOOR_UNIT_LABELS);
  }
  const packaged = all.filter(
    (component) => PACKAGED_UNIT_LABELS[component.type ?? ''] !== undefined
  );
  return pickFrom(packaged, PACKAGED_UNIT_LABELS);
}

// Explicit = a user statement: a manual/union pick, or ANY record the
// conflict dialog resolved (resolvedCalcTons present). Explicit records win
// precedence and survive the fingerprint overwrite (§B row 9, approved
// deviation); everything else is a derivation that re-derives live.
export function isExplicitTonnageRecord(
  record: TonnageRecord | null | undefined
): boolean {
  return (
    !!record &&
    (record.source === 'manual' ||
      record.source === 'union' ||
      record.resolvedCalcTons != null)
  );
}

// Precedence (§B, revised): most-recent-explicit with a size wins; otherwise
// the load beats the old unit — a Layout & Sizing calc sizes the home, and
// the existing equipment is the fallback only while no calc exists. That is
// the same precedence the server's requirement ladder uses to measure
// coverage and compute the sizing bands, so the size chip can never
// contradict the band it is filtering alongside (an existing unit is more
// often oversized than not). Unsure (sizingPath block-load) stays none. An
// empty explicit record — leftover from clearing a tile or toggling Unsure
// off — is not a size statement, so it falls through to live derivation. A
// non-explicit record's stored tons are ignored (they were a snapshot taken
// to carry sizingPath/filterDismissed): the live derivation is the truth.
export function resolveEffectiveTonnage(args: {
  record: TonnageRecord | null | undefined;
  equipment: EquipmentTonnage | null | undefined;
  calc: CalcTonnage | null | undefined;
}): EffectiveTonnage | null {
  const { record, equipment, calc } = args;
  if (isUnsureTonnage(record)) return null;
  if (record && isExplicitTonnageRecord(record) && record.tons.length > 0) {
    return { tons: sortedUnique(record.tons), source: record.source };
  }
  if (calc) return { tons: calc.tons, source: 'calc' };
  if (equipment) return { tons: [equipment.tile], source: 'equipment' };
  return null;
}

// Tile-space containment: single pick → equality; span → calc tile inside
// [min, max] inclusive.
export function tonnageSpanContains(tons: number[], tile: number): boolean {
  const selected = sortedUnique(tons);
  if (selected.length === 0) return false;
  return tile >= selected[0] && tile <= selected[selected.length - 1];
}

// Whether two selections share at least one size — a pick agrees with the
// calc when it is one of the sizes the load takes.
export function tonnageSpansOverlap(a: number[], b: number[]): boolean {
  const left = sortedUnique(a);
  const right = sortedUnique(b);
  if (left.length === 0 || right.length === 0) return false;
  return (
    Math.max(left[0], right[0]) <=
    Math.min(left[left.length - 1], right[right.length - 1])
  );
}

// How the old unit sits against the load's sizes: 'larger' or 'smaller'
// when it is outside them, null when it is one of them (or there is nothing
// to compare) — the phrase the size surfaces use to say why the calc, not
// the equipment, is the size story.
export function equipmentVersusCalc(
  equipment: EquipmentTonnage | null | undefined,
  calc: CalcTonnage | null | undefined
): 'larger' | 'smaller' | null {
  if (!equipment || !calc || calc.tons.length === 0) return null;
  if (equipment.tile > calc.tons[calc.tons.length - 1]) return 'larger';
  if (equipment.tile < calc.tons[0]) return 'smaller';
  return null;
}

// Conflict-dialog trigger predicate (ruling 5): calc present ∧ a selection
// exists ∧ the selection shares no size with the calc ∧ not already
// acknowledged for this calc tile (resolvedCalcTons guards the re-trigger —
// only a NEW calc tile re-arms). The ducted-only gate is the caller's. A
// derived calc selection is itself the calc, so only explicit picks and the
// use-equipment path can conflict.
export function shouldOpenTonnageConflict(args: {
  effective: EffectiveTonnage | null;
  calc: CalcTonnage | null;
  record: TonnageRecord | null | undefined;
}): boolean {
  const { effective, calc, record } = args;
  if (!calc || !effective) return false;
  if (tonnageSpansOverlap(effective.tons, calc.tons)) return false;
  return record?.resolvedCalcTons !== calc.tile;
}

// Dialog resolution (mock S4): keep-current keeps the effective span and
// source, use-calculated adopts the calc's sizes, BOTH unions into the
// contiguous span with source 'union'. Every resolution stamps
// resolvedCalcTons (the row-7/8 guard) and carries the record's
// sizingPath/filterDismissed forward. Neither checked = no resolution
// (callers keep the dialog open — confirm is disabled).
export function resolveTonnageConflict(args: {
  effective: EffectiveTonnage;
  calc: CalcTonnage;
  record: TonnageRecord | null | undefined;
  keepCurrent: boolean;
  useCalc: boolean;
}): TonnageRecord | null {
  const { effective, calc, record, keepCurrent, useCalc } = args;
  if (!keepCurrent && !useCalc) return null;
  let tons: number[];
  let source: TonnageSource;
  if (keepCurrent && useCalc) {
    const combined = sortedUnique([...effective.tons, ...calc.tons]);
    tons = tonnageSpan(combined[0], combined[combined.length - 1]);
    source = 'union';
  } else if (useCalc) {
    tons = calc.tons;
    source = 'calc';
  } else {
    tons = sortedUnique(effective.tons);
    source = effective.source;
  }
  return {
    tons,
    source,
    ...(useCalc
      ? withSizingPathAfterManualSize(record)
      : carryTonnageFlags(record)),
    resolvedCalcTons: calc.tile
  };
}

export function adoptCalcTonnageRecord(
  record: TonnageRecord | null | undefined,
  calc: CalcTonnage
): TonnageRecord {
  return {
    tons: calc.tons,
    source: 'calc',
    ...carryTonnageFlags(record),
    sizingPath: null,
    resolvedCalcTons: calc.tile
  };
}

// The size story Choose Systems actually filters to: 'use-equipment' is
// the live old-unit size even when the form tiles still show a different
// pick, and Unsure resolves to the live L&S calc (the size it deferred to)
// as soon as one exists. Review / conflict compare against THIS, not form
// `effective`.
export function resolveFilterEffectiveTonnage(args: {
  record: TonnageRecord | null | undefined;
  equipment: EquipmentTonnage | null | undefined;
  calc: CalcTonnage | null | undefined;
}): EffectiveTonnage | null {
  if (args.record?.sizingPath === 'use-equipment' && args.equipment) {
    return { tons: [args.equipment.tile], source: 'equipment' };
  }
  if (isUnsureTonnage(args.record)) {
    return args.calc ? { tons: args.calc.tons, source: 'calc' } : null;
  }
  return resolveEffectiveTonnage(args);
}

// Phrase source for the CS size-filter chip: 'use-equipment' is the old
// unit even when the form tiles still show a different pick, and Unsure
// phrases as the calc it deferred to.
export function tonnageFilterSource(
  record: TonnageRecord | null | undefined,
  equipment: EquipmentTonnage | null | undefined,
  effective: EffectiveTonnage | null,
  calc: CalcTonnage | null = null
): TonnageSource | undefined {
  return (
    resolveFilterEffectiveTonnage({ record, equipment, calc })?.source ??
    effective?.source
  );
}

// Source phrase for the row's hint bar and the CS chip (§B labels).
export function tonnageSourcePhrase(
  source: TonnageSource,
  options: { detailedEstimate?: boolean } = {}
): string {
  switch (source) {
    case 'equipment':
      return 'from your equipment';
    case 'calc':
      return options.detailedEstimate
        ? 'based on your detailed sizing estimate'
        : 'from your Layout & Sizing calculation';
    case 'union':
      return 'your pick + calculation';
    default:
      return 'your pick';
  }
}

// GUI-side read: shared structural parse, then scale normalization so a
// re-persist can never 400. tons entries must sit on the tile scale; a
// non-empty list normalizes to its contiguous [min, max] span, and a list
// whose entries ALL fell off-scale drops the record rather than fabricating
// an explicit clear.
export function parseSavedTonnage(raw: unknown): TonnageRecord | null {
  const record = parseTonnageRecord(raw);
  if (!record) return null;
  const onScale = sortedUnique(
    record.tons.filter((value) => TONNAGE_TILE_TONS.includes(value))
  );
  if (record.tons.length > 0 && onScale.length === 0) return null;
  return {
    ...record,
    tons:
      onScale.length > 0
        ? tonnageSpan(onScale[0], onScale[onScale.length - 1])
        : []
  };
}

// The size Choose Systems would filter to (§C), ignoring the "Show all
// sizes" escape. 'use-equipment' seeds from the live old-unit size so the
// form's selected tiles can stay on a different pick.
export function resolveTonnageFilterRange(args: {
  record: TonnageRecord | null | undefined;
  components: TonnageSourceComponent[] | null | undefined;
  coolingBtu: number | null | undefined;
}): { min: number; max: number } | null {
  const filterEffective = resolveFilterEffectiveTonnage({
    record: args.record,
    equipment: deriveEquipmentTonnage(args.components),
    calc: calcTonnageFromBtu(args.coolingBtu)
  });
  if (!filterEffective) return null;
  return {
    min: filterEffective.tons[0],
    max: filterEffective.tons[filterEffective.tons.length - 1]
  };
}

// The Choose Systems size-filter seed for one system (§C): the filter
// range above — unless the user took the "Show all sizes" escape
// (filterDismissed persists so a reload never re-ambushes an opt-out).
export function resolveTonnageSeed(args: {
  record: TonnageRecord | null | undefined;
  components: TonnageSourceComponent[] | null | undefined;
  coolingBtu: number | null | undefined;
}): { min: number; max: number } | null {
  if (args.record?.filterDismissed) return null;
  return resolveTonnageFilterRange(args);
}
